// AIVA Material+ Design System Tokens
// This file contains the complete design system configuration

export const tokens = {
  // Color palette
  colors: {
    primary: {
      50: '#E6FAF5',
      100: '#CCF5EB',
      200: '#99EBD7',
      300: '#66E0C3',
      400: '#33D6AF',
      500: '#00D4AA', // Main brand
      600: '#00AA88',
      700: '#008066',
      800: '#005544',
      900: '#002B22',
    },
    secondary: {
      50: '#F3E5F5',
      100: '#E1BEE7',
      200: '#CE93D8',
      300: '#BA68C8',
      400: '#AB47BC',
      500: '#9C27B0',
      600: '#8E24AA',
      700: '#7B1FA2',
      800: '#6A1B9A',
      900: '#4A148C',
    },
    accent: {
      50: '#FFF3E0',
      100: '#FFE0B2',
      200: '#FFCC80',
      300: '#FFB74D',
      400: '#FFA726',
      500: '#FF9800',
      600: '#FB8C00',
      700: '#F57C00',
      800: '#EF6C00',
      900: '#E65100',
    },
    neutral: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    success: {
      50: '#E8F5E9',
      500: '#4CAF50',
      700: '#388E3C',
    },
    warning: {
      50: '#FFF3E0',
      500: '#FF9800',
      700: '#F57C00',
    },
    error: {
      50: '#FFEBEE',
      500: '#F44336',
      700: '#D32F2F',
    },
    info: {
      50: '#E3F2FD',
      500: '#2196F3',
      700: '#1976D2',
    },
  },

  // Typography
  typography: {
    fontFamily: {
      sans: ['Inter', 'system-ui', '-apple-system', 'sans-serif'],
      display: ['Poppins', 'Inter', 'system-ui', 'sans-serif'],
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem',// 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
    },
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Spacing scale (based on 4px grid)
  spacing: {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
  },

  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.25rem',   // 4px
    base: '0.5rem',  // 8px
    md: '0.75rem',   // 12px
    lg: '1rem',      // 16px
    xl: '1.5rem',    // 24px
    '2xl': '2rem',   // 32px
    full: '9999px',
  },

  // Shadows
  shadows: {
    soft: '0 2px 8px rgba(0, 0, 0, 0.08)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.12)',
    large: '0 8px 32px rgba(0, 0, 0, 0.16)',
    glass: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    elevated: '0 12px 48px rgba(0, 0, 0, 0.20)',
  },

  // Animation
  animation: {
    duration: {
      fast: '150ms',
      base: '300ms',
      slow: '500ms',
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },

  // Z-index scale
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    modalBackdrop: 1300,
    modal: 1400,
    popover: 1500,
    tooltip: 1600,
  },

  // Breakpoints (mobile-first)
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

// Gradient presets
export const gradients = {
  primary: 'linear-gradient(135deg, #00D4AA 0%, #00AA88 100%)',
  secondary: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
  accent: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
  warm: 'linear-gradient(135deg, #FFE5CC 0%, #FFF4ED 100%)',
  cool: 'linear-gradient(135deg, #E6FAF5 0%, #CCF5EB 100%)',
  glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
  sunset: 'linear-gradient(135deg, #FF9800 0%, #9C27B0 100%)',
  ocean: 'linear-gradient(135deg, #00D4AA 0%, #2196F3 100%)',
} as const;

// Component-specific token mappings
export const componentTokens = {
  button: {
    height: {
      sm: '32px',
      md: '40px',
      lg: '48px',
    },
    padding: {
      sm: '0 12px',
      md: '0 16px',
      lg: '0 24px',
    },
    fontSize: {
      sm: tokens.typography.fontSize.sm,
      md: tokens.typography.fontSize.base,
      lg: tokens.typography.fontSize.lg,
    },
  },
  card: {
    padding: {
      sm: tokens.spacing[4],
      md: tokens.spacing[6],
      lg: tokens.spacing[8],
    },
    borderRadius: {
      sm: tokens.borderRadius.lg,
      md: tokens.borderRadius.xl,
      lg: tokens.borderRadius['2xl'],
    },
  },
  input: {
    height: {
      sm: '36px',
      md: '44px',
      lg: '52px',
    },
    borderRadius: tokens.borderRadius.lg,
  },
} as const;

// Merchant brand customization interface
export interface MerchantTheme {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  logo?: string;
  favicon?: string;
  fontFamily?: string;
  borderRadius?: 'sharp' | 'medium' | 'rounded';
  customCSS?: string;
}

// Default merchant themes (examples)
export const merchantThemes: Record<string, MerchantTheme> = {
  myfurbaby: {
    primaryColor: '#00D4AA',
    secondaryColor: '#FF9800',
    accentColor: '#9C27B0',
    borderRadius: 'rounded',
  },
  demo: {
    primaryColor: '#00D4AA',
    secondaryColor: '#9C27B0',
    accentColor: '#FF9800',
    borderRadius: 'rounded',
  },
} as const;

// Helper function to apply merchant theme
export function applyMerchantTheme(merchantId: string, customTheme?: MerchantTheme) {
  const theme = customTheme || merchantThemes[merchantId] || merchantThemes.demo;

  if (theme.primaryColor) {
    document.documentElement.style.setProperty('--color-primary', theme.primaryColor);
  }

  if (theme.secondaryColor) {
    document.documentElement.style.setProperty('--color-secondary', theme.secondaryColor);
  }

  if (theme.accentColor) {
    document.documentElement.style.setProperty('--color-accent', theme.accentColor);
  }

  if (theme.fontFamily) {
    document.documentElement.style.setProperty('--font-sans', theme.fontFamily);
  }

  if (theme.customCSS) {
    const styleEl = document.createElement('style');
    styleEl.id = 'merchant-custom-css';
    styleEl.textContent = theme.customCSS;
    document.head.appendChild(styleEl);
  }
}

export type Theme = typeof tokens;
export default tokens;
